import React, { useState, useEffect, useContext, useRef } from 'react';
import { Table } from '@mui/material';
import { GlobalContext } from '../../../App';
import ColumnFilter from './ColumnFilter';
import TableActions from './TableActions';

/* קומפוננטות שפיצלנו */
import GenericTableHeader from './GenericTableHeader';
import GenericTableBody from './GenericTableBody';
import GenericTablePagination from './GenericTablePagination';

const GenericTable = ({
  columns,
  defaultRows = [],
  onRowsChange,
  searchText,
  onRowSelect = () => {},
  defaultHeight = '400px'
}) => {
  const { permission } = useContext(GlobalContext);
  const lockedPermissions = [];
  const requiredPermissions = [1];

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);

  const [rows, setRows] = useState(() => {
    return defaultRows.length > 0 ? [...defaultRows] : [];
  });

  const [columnWidths, setColumnWidths] = useState(
    columns.map((column) => column.width || 150)
  );

  const [selectedRows, setSelectedRows] = useState({ rows: [], lastClick: null });
  const [editableRows, setEditableRows] = useState([]);
  const [updatedColumns, setUpdatedColumns] = useState(columns);
  const [filters, setFilters] = useState({});
  const tableRef = useRef(null);

  // Pagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

    // useEffect לסינון והגדרת הרשומות לפי ה-searchText
    useEffect(() => {
      if (searchText) {
        setRows(handleFilter(filters, searchText));
      }
    }, [searchText]);
    
  // אפקט לעדכון קומפוננטה אב
  useEffect(() => {
    onRowsChange(rows);
  }, [rows]);

  // אפקט לסנכרון בחירות לאחר סינון/עריכה
  useEffect(() => {
    const updatedSelectedRows = selectedRows.rows.filter((r) => rows.includes(r));
    setSelectedRows({ ...selectedRows, rows: updatedSelectedRows });
  }, [rows]);

  const handleFilter = (filters, data) => {
    let newData = [...data];    
        // בדיקה של כל פילטר והפעלת הסינון בהתאם
        Object.keys(filters).forEach((field) => {
          const filter = filters[field];
          const { type, value, extraValue } = filter;
    
          if (type === 'equal' && value) {
            newData = newData.filter((row) => String(row[field]) === value);
          } else if (type === 'startsWith' && value) {
            newData = newData.filter((row) => String(row[field]).startsWith(value));
          } else if (type === 'endsWith' && value) {
            newData = newData.filter((row) => String(row[field]).endsWith(value));
          } else if (type === 'contains' && value) {
            newData = newData.filter((row) => String(row[field]).includes(value));
          } else if (type === 'notContains' && value) {
            newData = newData.filter((row) => !String(row[field]).includes(value));
          } else if (type === 'specificDate' && value) {            
            newData = newData.filter((row, i) => {
              const rowDate = new Date(row[field]);
              // בדיקה אם הערך אינו תאריך תקין או שהוא ריק/00/00/0000
              if (isNaN(rowDate.getTime()) || !row[field] || row[field] === '0000-00-00') {
                return false; // הסר את הרשומה
              }
              // שמור רק ערכים שגדולים או שווים לתאריך שנבחר
              // return rowDate === new Date(value);
              return rowDate.getTime() === new Date(value).getTime();

            });
          } else if (type === 'startDate' && value) {
            newData = newData.filter((row, i) => {
              const rowDate = new Date(row[field]);
              // בדיקה אם הערך אינו תאריך תקין או שהוא ריק/00/00/0000
              if (isNaN(rowDate.getTime()) || !row[field] || row[field] === '0000-00-00') {
                return false; // הסר את הרשומה
              }
              // שמור רק ערכים שגדולים או שווים לתאריך שנבחר
              return rowDate >= new Date(value);
            });
            handleFirstPage()
          } else if (type === 'endDate' && value) {
            newData = newData.filter((row, i) => {
              const rowDate = new Date(row[field]);
              // בדיקה אם הערך אינו תאריך תקין או שהוא ריק/00/00/0000
              if (isNaN(rowDate.getTime()) || !row[field] || row[field] === '0000-00-00') {
                return false; // הסר את הרשומה
              }
              // שמור רק ערכים שגדולים או שווים לתאריך שנבחר
              return rowDate <= new Date(value);
            });
            handleFirstPage()
          } else if (type === 'dateRange' && value && extraValue) {
            newData = newData.filter((row) => {
              const rowDate = new Date(row[field]);
              return rowDate >= new Date(value) && rowDate <= new Date(extraValue);
            });
          } else if (type === 'asc') {
            // מיון א'-ב'
            newData.sort((a, b) => String(a[field]).localeCompare(String(b[field]), 'he'));
          } else if (type === 'desc') {
            // מיון ב'-א'
            newData.sort((a, b) => String(b[field]).localeCompare(String(a[field]), 'he'));
          } else if (type === 'newest') {
            newData.sort((a, b) => {
              const dateA = new Date(a[field]);
              const dateB = new Date(b[field]);
          
              const invalidA = isNaN(dateA.getTime()) || !a[field] || a[field] === '0000-00-00';
              const invalidB = isNaN(dateB.getTime()) || !b[field] || b[field] === '0000-00-00';
          
              // שניהם ריקים => שוויון
              if (invalidA && invalidB) return 0;
              // אם A ריק אבל B לא ריק => למקם את A בסוף => החזרה חיובית
              if (invalidA && !invalidB) return 1;
              // אם A לא ריק אבל B ריק => למקם את B בסוף => החזרה שלילית
              if (!invalidA && invalidB) return -1;
          
              // שניהם תקינים => מיון יורד (גדול->קטן), newest first
              return dateB - dateA;
            });
            handleFirstPage()
          } else if (type === 'oldest') {
            newData.sort((a, b) => {
              const dateA = new Date(a[field]);
              const dateB = new Date(b[field]);
          
              const invalidA = isNaN(dateA.getTime()) || !a[field] || a[field] === '0000-00-00';
              const invalidB = isNaN(dateB.getTime()) || !b[field] || b[field] === '0000-00-00';
          
              // שניהם ריקים => שוויון
              if (invalidA && invalidB) return 0;
              // אם A ריק אבל B לא ריק => למקם את A בהתחלה => החזרה שלילית
              if (invalidA && !invalidB) return -1;
              // אם A לא ריק אבל B ריק => למקם את B בהתחלה => החזרה חיובית
              if (!invalidA && invalidB) return 1;
          
              // שניהם תקינים => מיון עולה (קטן->גדול), oldest first
              return dateA - dateB;
            });
            handleFirstPage()
          }
        });
        if (!filters || Object.keys(filters).length === 0) {
          // אם אין פילטרים כלל, חזור לנתוני ברירת המחדל
          newData = [...data];
        }
    return newData
  }

  // פונקציות הרשאה
  const hasLockedPermission = () =>
    permission === 0 || lockedPermissions.includes(permission);
  const hasRequiredPermission = () =>
    permission === 0 || requiredPermissions.includes(permission);

  // פונקציית פתיחה/סגירה של תפריט סינון
  const handleFilterClick = (e, column) => {
    setAnchorEl(e.currentTarget);
    setFilterColumn(column);
  };
  const handleFilterClose = () => {
    setAnchorEl(null);
    setFilterColumn(null);
  };

  // שינוי ערך בפילטר (לא ממומש במלואו)
  const handleFilterChange = (filterType, value, extraValue = null) => {

    const updatedFilters = {
      ...filters,
      [filterColumn.field]: {
        ...filters[filterColumn.field],
        type: filterType,
        value,
        extraValue
      }
    };

    if (value === '') {
      delete updatedFilters[filterColumn.field]
    }

    setFilters(updatedFilters);
    // ... לוגיקת סינון/מיון
    setRows([...handleFilter(updatedFilters, defaultRows)])

  };

  // שינוי ערך בתוך שורה (בעריכה)
  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  // בחירת שורה
  const handleRowSelection = (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    let newSelected = [];
    if (selectedRows.rows.includes(row)) {
      newSelected = selectedRows.rows.filter((r) => r !== row);
    } else {
      newSelected = [...selectedRows.rows, row];
    }
    setSelectedRows({ ...selectedRows, rows: newSelected });
  };

  // לחיצה (בודקת האם דאבל קליק)
  const handleRowClick = (rowId) => {
    const currentTime = new Date().getTime();
    if (selectedRows.lastClick && currentTime - selectedRows.lastClick < 300) {
      onRowSelect([rows.find((row) => row.id === rowId)]);
      setSelectedRows({ ...selectedRows, lastClick: null });
    } else {
      handleRowSelection(rowId);
      setSelectedRows({ ...selectedRows, lastClick: currentTime });
    }
  };

  // מחיקת שורה
  const handleDeleteRow = (index) => {
    const rowToDelete = rows[index];
    if (rowToDelete.locked && !hasLockedPermission()) {
      alert('לא ניתן למחוק רשומה נעולה.');
      return;
    }
    if (rowToDelete.required && !hasRequiredPermission()) {
      alert('לא ניתן למחוק רשומה חובה.');
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  // עריכת שורה
  const handleEditRow = (rowIndex) => {
    if (editableRows.includes(rowIndex)) {
      setEditableRows(editableRows.filter((i) => i !== rowIndex));
    } else {
      setEditableRows([...editableRows, rowIndex]);
    }
  };

  // שינוי מספר שורות בדף
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1);
  };

  // ניווט
  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(Math.ceil(rows.length / rowsPerPage));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(rows.length / rowsPerPage)));
  const handlePreviousPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 1));

  // חישוב השורות שמוצגות
  const currentRows = rows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // שינוי רוחב עמודה (גרירה)
  const handleMouseDown = (index, e) => {
    const startX = e.clientX;
    const startWidth = columnWidths[index];

    const onMouseMove = (e) => {
      const deltaX = startX - e.clientX;
      const newWidths = [...columnWidths];
      newWidths[index] = Math.max(startWidth + deltaX, 120);
      setColumnWidths(newWidths);

      const newUpdatedColumns = [...updatedColumns];
      newUpdatedColumns[index] = {
        ...newUpdatedColumns[index],
        width: newWidths[index]
      };
      setUpdatedColumns(newUpdatedColumns);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // פונקציה לבחירה/ביטול בחירה של כל השורות
  const handleSelectAllToggle = () => {
    if (selectedRows.rows.length === rows.length) {
      setSelectedRows({ rows: [], lastClick: null });
    } else {
      setSelectedRows({ rows: [...rows], lastClick: null });
    }
  };

  // פונקציה שמוחקת את הפילטר עבור שדה מסוים
const handleFilterClear = (field) => {
  const updatedFilters = { ...filters };
  // מסיר את המפתח מהאובייקט
  // delete updatedFilters[field];
  console.log(1,filters);
  console.log(2,updatedFilters);
  console.log(2.1, field);
  
  
  delete updatedFilters[field]
  console.log(3,updatedFilters);
  // מעדכן את הסטייט של הפילטרים
  setFilters(updatedFilters);

  setRows(handleFilter(updatedFilters, defaultRows))

  // כאן, אם אתה מנהל סינון בזמן-אמת, ייתכן שתצטרך
  // לקרוא לפונקציה שמסננת מחדש את השורות (לדוגמה setRows ל- originalRows או משהו דומה).
  // לדוגמה:
  // setRows(originalRows); 
  // או setRows(סינון_לפי_כל_מה_שנשאר_ב־updatedFilters);
};


  return (
    <div style={{ width: '100%' }}>
      {/* פעולות כלליות מעל הטבלה */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <TableActions columns={updatedColumns} rows={rows} 
        printRef={tableRef}
         />
      </div>

      {/* אזור גלילה */}
      <div style={{ width: '100%', minHeight: defaultHeight, maxHeight: defaultHeight, overflowY: 'auto' }}
        ref={tableRef}
      >
        <Table style={{ width: '100%', tableLayout: 'auto', position: 'relative' }} size="small">
          
          {/* Header (כותרת) */}
          <GenericTableHeader
            columns={columns}
            columnWidths={columnWidths}
            selectedRows={selectedRows.rows}
            rows={rows}
            handleMouseDown={handleMouseDown}
            handleFilterClick={handleFilterClick}
            onSelectAllToggle={handleSelectAllToggle}
            filters={filters}
          />

          {/* Body (שורות) */}
          <GenericTableBody
            currentRows={currentRows}
            columns={columns}
            columnWidths={columnWidths}
            selectedRows={selectedRows.rows}
            editableRows={editableRows}
            hasLockedPermission={hasLockedPermission}
            hasRequiredPermission={hasRequiredPermission}
            onRowClick={handleRowClick}
            onRowSelect={handleRowSelection}
            onRowEdit={handleEditRow}
            onRowDelete={handleDeleteRow}
            onInputChange={handleInputChange}
          />
        </Table>
      </div>

      {/* סרגל תחתון - מידע על מספר השורות שנבחרו, ופג'ינציה */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'center' }}>
        <div
          style={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '10px 15px'
          }}
        >
          {`${selectedRows.rows.length} מתוך ${rows.length} שורות נבחרו`}
        </div>

        <div style={{ display: 'flex' }}>
          <GenericTablePagination
            rows={rows}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            onFirstPage={handleFirstPage}
            onLastPage={handleLastPage}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
          />
        </div>
      </div>

      {/* תפריט פילטר (ColumnFilter) */}
      {filterColumn && (
        <ColumnFilter
          anchorEl={anchorEl}
          onClose={handleFilterClose}
          onFilterChange={handleFilterChange}
          columnType={filterColumn?.input}
          filterValues={filters[filterColumn?.field] || {}}
          // שני פרופסים חדשים:
          filterField={filterColumn.field}
          handleFilterClear={handleFilterClear}
        />
      )}
    </div>
  );
};

export default GenericTable;
